import { JSX, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityAggregateGroup, NetworkIntegrityAggregateKey, ProjectExclusionType, ProviderBadgeStyle } from "@app/projects/enumerators";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Link, useHistory, useRouteMatch } from "react-router-dom";
import { NetworkIntegrityFilterAction} from "@app/projects/network-assessment/network-integrity/state-management/networkIntegrityFilterStateManagement";
import {
  NetworkIntegrityProvider,
  NetworkIntegrityProviderSelectionMode,
  NetworkIntegrityProviderSelectionState,
  RouteMatchParams
} from "@app/projects/network-assessment/network-integrity/models";
import BadgedContent from "@app/shared/BadgedContent";
import { FlyoutLink } from "@app/shared/Flyouts";
import { QesColors } from "@app/shared/qes-standards/colors";
import { NetworkIntegrityTerminationFlyout } from "@app/projects/network-assessment/network-integrity/providers/NetworkIntegrityTerminationFlyout";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { generateProviderHubLink, isProviderHubAvailable, isProviderHubDataReady, PROVIDER_HUB_UNAVAILABLE_TOOLTIP } from "@app/projects/network-assessment/network-integrity/helpers/providerHubLinkHelper";
import { isHistoricalRun } from "@app/projects/network-assessment/network-integrity/helpers/historicalRunHelper";
import { isNpiTypeEnabled, toNpiTypeLabel } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityNpiTypeHelper";
import { Forbidden, NotFound } from "@app/shared/ErrorPages";
import { createEmptySelectionState, toSelectionDto } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityProviderSelectionStateHelper";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import Features from "@app/feature-flags/features";
import { formatDate } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { SearchBox } from "@app/shared/SearchBox";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { Waffles } from "@app/shared/waffles/Waffles";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import NetworkIntegrityFilterContainer from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityFilterContainer";
import { NetworkIntegrityActionStatus, NetworkIntegrityProviderTypes } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { StandardColumnWidths } from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityConstants";

interface NetworkIntegrityProviderTableData {
    [wafflesKeyColumnName]: string;
    address1: string;
    address2: string;
    city: string;
    npi: string;
    npiEntityType: string;
    dateDeactivated: string;
    locationConfidence: string;
    providerName: JSX.Element;
    specialty: string;
    specialtyName: string;
    state: string;
    zip: string;
    isServicing: string;
}

const NetworkIntegrityProvidersPage = (): JSX.Element => {
  const ffContext = useContext(FeatureFlagContext);
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const networkIntegrityContextRef = useRef(networkIntegrityContext);
  const project = networkIntegrityContext.project;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const routeMatch = useRouteMatch<RouteMatchParams>();
  const { aggregateGroup, projectId, projectRunConfigurationId, providerType: routeProviderType } = routeMatch.params;
  // React Router v5 does not decode reserved characters (e.g. `&` survives as `%26`) in path params.
  // Decode once at the boundary so the value round-trips correctly through the API and renders cleanly.
  const aggregateType = routeMatch.params.aggregateType
    ? decodeURIComponent(routeMatch.params.aggregateType)
    : routeMatch.params.aggregateType;
  const pageSize = 100;
  const user = useContext(UserContext);
  const history = useHistory();
  const showTermedStatus = ffContext.enabledFeatureFlags.has(Features.DISPLAY_NETWORK_INTEGRITY_TOTAL_AFTER_TERMED);
  const isTermAdmin = user.roles.includes(Roles.TERM_PROVIDER_ADMIN);
  const isTermViewer = user.roles.includes(Roles.TERM_PROVIDER_VIEWER);
  const isHistoricalRunView = isHistoricalRun(networkIntegrityContext.projectRunConfiguration);
  const canLinkToProviderHub = isProviderHubAvailable(ffContext.enabledFeatureFlags.has(Features.PROVIDER_HUB), project, networkIntegrityContext.projectRunConfiguration);
  const providerHubDataReady = isProviderHubDataReady(project, networkIntegrityContext.projectRunConfiguration);
  const showNpiType = isNpiTypeEnabled(ffContext.enabledFeatureFlags, aggregateGroup);
  const npiEntityTypeFilter = showNpiType ? networkIntegrityContext.npiType ?? null : null;

  const [providers, setProviders] = useState<NetworkIntegrityProvider[]>([]);
  // Selections persist across pages while `providers` holds only the current page.
  const [termedProviderKeys, setTermedProviderKeys] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [mainSelection, setMainSelection] = useState<NetworkIntegrityProviderSelectionState>(() => createEmptySelectionState());
  const [searchSelection, setSearchSelection] = useState<NetworkIntegrityProviderSelectionState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(0);
  // Bumped on a filter change so the page-fetch effect runs once, with the reset page and the new filters.
  const [filtersVersion, setFiltersVersion] = useState<number>(0);
  const [showConfidenceLevel, setShowConfidenceLevel] = useState(false);
  const [showForbiddenAlert, setShowForbiddenAlert] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortedColumn, setSortedColumn] = useState<string>("npi");
  const [sortDirection, setSortDirection] = useState<string>("asc");
  const [bulkCheckboxCheckedOverride, setBulkCheckboxCheckedOverride] = useState<boolean>(false);
  const [bulkCheckboxIndeterminateOverride, setBulkCheckboxIndeterminateOverride] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const hasSyncedUrlRef = useRef(false);
  const [uniqueAccessPointCount, setUniqueAccessPointCount] = useState<number>(0);
  const [projectIsRunning, setProjectIsRunning] = useState<boolean>(false);
  const [validAggregateKey, setValidAggregateKey] = useState<boolean>(true);

  const isSearchActive = searchTerm.length > 0 && searchSelection !== null;
  const activeSelection = isSearchActive ? searchSelection : mainSelection;

  useEffect(() => {
    if (!project) return;

    setProjectIsRunning(project.isRunning);
    if (shouldShowForbiddenAlert(project.showNetworkIntegrityCriticalAlerts, project.showNetworkIntegrityAttestationBasedAlerts, aggregateType)) {
      setShowForbiddenAlert(true);
      return;
    }

    const shouldShowConfidenceLevels = (project.showNetworkIntegrityCriticalAlerts ?? false)
      && (aggregateType == NetworkIntegrityAggregateKey.LowLocationConfidence
        || aggregateType == NetworkIntegrityAggregateKey.VeryLowLocationConfidence
        || aggregateGroup == NetworkIntegrityAggregateGroup.ExcessAddresses);

    setShowConfidenceLevel(shouldShowConfidenceLevels);

    let cancelled = false;
    (async () => {
      const count = await networkIntegrityService.getAggregateCount(
        Number(projectId),
        networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId,
        routeProviderType,
        aggregateGroup,
        aggregateType,
        networkIntegrityContext.regionSetIds ?? [],
        networkIntegrityContext.specialtyCodes ?? [],
        networkIntegrityContext.actionStatus);
      if (!cancelled) {
        setUniqueAccessPointCount(count);
      }
    })();

    return () => { cancelled = true; };
  }, [project, projectId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const count = await networkIntegrityService.getAggregateCount(
        Number(projectId),
        networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId,
        routeProviderType,
        aggregateGroup,
        aggregateType,
        networkIntegrityContext.regionSetIds ?? [],
        networkIntegrityContext.specialtyCodes ?? [],
        networkIntegrityContext.actionStatus);
      if (!cancelled) setUniqueAccessPointCount(count);
    })();

    if (networkIntegrityContext.isLoading || isInitialized) {
      return () => { cancelled = true; };
    }

    if (routeProviderType && networkIntegrityContext.providerType !== routeProviderType) {
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.ProviderTypeFilterChanged,
        value: { providerType: routeProviderType }
      });
    }

    setIsInitialized(true);

    return () => { cancelled = true; };
  }, [networkIntegrityContext.isLoading, routeProviderType, isInitialized]);

  async function fetchProvidersForCurrentPage(cancelled: { value: boolean }) {
    setLoading(true);

    const { providers, totalCount, validAggregateKey } = await networkIntegrityService.getProviders(
      Number(projectId),
      networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId,
      networkIntegrityContext.providerType,
      aggregateGroup,
      aggregateType,
      currentPage,
      pageSize,
      searchTerm || undefined,
      sortedColumn.toString(),
      sortDirection.toString(),
      networkIntegrityContext.regionSetIds ?? [],
      networkIntegrityContext.specialtyCodes ?? [],
      networkIntegrityContext.actionStatus,
      npiEntityTypeFilter);

    if (!cancelled.value) {
      setProviders(providers);
      setTermedProviderKeys(previous => {
        const updated = new Set(previous);
        providers.filter(p => p.isTermed).forEach(p => updated.add(`${p.providerId}|${p.providerSetVersionId}`));
        return updated;
      });
      setTotalCount(totalCount);
      setValidAggregateKey(validAggregateKey);
      setLoading(false);
    }
  };

  // Only provider type and run configuration live in the URL, so only they push a history entry.
  useEffect(() => {
    if (!isInitialized || networkIntegrityContext.isLoading) {
      return;
    }

    const isMountSync = !hasSyncedUrlRef.current;
    hasSyncedUrlRef.current = true;

    const currentRunConfigId = networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId;
    // Compare route params, not pathnames: history decodes location.pathname, so it never equals an encodeURIComponent-built path.
    if (routeProviderType === networkIntegrityContext.providerType && projectRunConfigurationId === String(currentRunConfigId)) {
      return;
    }

    const path = `/projects/${projectId}/${currentRunConfigId}/network-integrity/providers/${networkIntegrityContext.providerType}/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(aggregateType)}`;
    // A mismatch found on load is reconciliation, not a user navigation, so it must not add a Back entry.
    if (isMountSync) {
      history.replace(path);
    } else {
      history.push(path);
    }
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, isInitialized, networkIntegrityContext.isLoading]);

  useEffect(() => {
    if (!isInitialized || networkIntegrityContext.isLoading) {
      return;
    }

    const cancelled = { value: false };

    // Reset selection state when filters, provider type, or run config change
    setSearchTerm("");
    setMainSelection(createEmptySelectionState());
    setSearchSelection(null);
    setSortedColumn("npi");
    setSortDirection("asc");
    setCurrentPage(0);
    setBulkCheckboxCheckedOverride(false);
    setBulkCheckboxIndeterminateOverride(false);
    setFiltersVersion(version => version + 1);

    (async () => {
      const count = await networkIntegrityService.getAggregateCount(
        Number(projectId),
        networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId,
        networkIntegrityContext.providerType,
        aggregateGroup,
        aggregateType,
        networkIntegrityContext.regionSetIds ?? [],
        networkIntegrityContext.specialtyCodes ?? [],
        networkIntegrityContext.actionStatus,
        npiEntityTypeFilter);
      if (!cancelled.value) setUniqueAccessPointCount(count);
    })();

    return () => { cancelled.value = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, networkIntegrityContext.actionStatus, networkIntegrityContext.npiType, isInitialized, networkIntegrityContext.isLoading]);

  useEffect(() => {
    if (!isInitialized || networkIntegrityContext.isLoading) return;

    const cancelled = { value: false };

    if (!searchSelection) {
      updateBulkCheckboxState(mainSelection);
    }

    fetchProvidersForCurrentPage(cancelled);

    return () => { cancelled.value = true; };
  }, [currentPage, sortedColumn, sortDirection, searchTerm, filtersVersion]);

  useEffect(() => {
    networkIntegrityContextRef.current = networkIntegrityContext;
  });

  useEffect(() => {
    const unsubscribe = history.listen((location, action) => {
      // because url is pushed on provider type change, listen for back/forward navigation to update the state accordingly
      if (action === "POP" && location.pathname.includes("/network-integrity/providers/")) {
        const pathParts = location.pathname.split('/');
        const newProviderRunConfigurationId = pathParts[3];
        const newProviderType = pathParts[6];

        setSearchTerm("");
        setMainSelection(createEmptySelectionState());
        setSearchSelection(null);
        setBulkCheckboxCheckedOverride(false);
        setBulkCheckboxIndeterminateOverride(false);
        setSortedColumn("npi");
        setSortDirection("asc");
        setCurrentPage(0);

        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProviderTypeFilterChanged, value: { providerType: newProviderType } });
        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: newProviderRunConfigurationId } });
      } else if (action == "POP" && location.pathname.includes(`/${projectId}/network-integrity`)) {
        // if navigating back goes to the main network integrity page, update the provider type there as well
        // Replace, not push: the user is already on this entry, and a push would duplicate it and drop Forward.
        const currentContext = networkIntegrityContextRef.current;
        history.replace({
          pathname: `/projects/${projectId}/network-integrity`,
          state: { initialProviderType: currentContext.providerType, initialRegionSetIds: currentContext.regionSetIds, initialSpecialtyCodes: currentContext.specialtyCodes, runConfiguration: currentContext.projectRunConfiguration }
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [history.location.pathname]);

  const fetchSelectedProviders = useCallback(async (): Promise<NetworkIntegrityProvider[]> => {
    let selection = activeSelection;
    if (isSearchActive && searchSelection) {
      selection = await mergeSearchToMainSelection(activeSelection);
    }
    const selectionDto = toSelectionDto(selection, /*searchTerm*/ undefined, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, networkIntegrityContext.actionStatus, npiEntityTypeFilter);
    const result = await networkIntegrityService.getSelectedProviders(
      Number(projectId),
      networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId,
      networkIntegrityContext.providerType,
      aggregateGroup,
      aggregateType,
      selectionDto
    );
    return result;
  }, [mainSelection, searchSelection, projectId, projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, networkIntegrityContext.actionStatus, npiEntityTypeFilter, aggregateGroup, aggregateType]);

  // Define aggregate keys that require critical alerts to be enabled
  const requiresCriticalAlerts: string[] = [
    NetworkIntegrityAggregateKey.GhostProviders,
    NetworkIntegrityAggregateKey.ExpiredLicensePractitioners,
    NetworkIntegrityAggregateKey.LowLocationConfidence,
    NetworkIntegrityAggregateKey.VeryLowLocationConfidence,
    NetworkIntegrityAggregateKey.LowSpecialtyEvidence
  ];

  // Define aggregate keys that require attestation-based alerts to be enabled
  const requiresAttestationBasedAlerts: string[] = [
    NetworkIntegrityAggregateKey.ClosedLocation,
    NetworkIntegrityAggregateKey.RetiredPractitioner,
    NetworkIntegrityAggregateKey.LeftOffice,
    NetworkIntegrityAggregateKey.NotRecognized,
    NetworkIntegrityAggregateKey.NoLongerOnRoster,
    NetworkIntegrityAggregateKey.TermedOnRoster
  ];

  function shouldShowForbiddenAlert(
    showNetworkIntegrityCriticalAlerts: boolean,
    showNetworkIntegrityAttestationBasedAlerts: boolean,
    aggregateKey: string
  ): boolean {
    if (!showNetworkIntegrityCriticalAlerts && (requiresCriticalAlerts.includes(aggregateKey) || aggregateKey.toLowerCase().startsWith(NetworkIntegrityAggregateKey.GhostProviders.toLowerCase()))) {
      return true;
    }

    if (!showNetworkIntegrityAttestationBasedAlerts && requiresAttestationBasedAlerts.includes(aggregateKey)) {
      return true;
    }

    return false;
  }

  /* oxlint-disable sort-keys */

  const getTableDataItem = (provider: NetworkIntegrityProvider): NetworkIntegrityProviderTableData => {
    const baseProviderNameContent = canLinkToProviderHub && provider.isServicing
                ? providerHubDataReady
                  ? generateProviderHubLink(projectId, networkIntegrityContext.projectRunConfigurationId, provider)
                  : <TooltipWrapper text={PROVIDER_HUB_UNAVAILABLE_TOOLTIP}>
                      <a href="#" onClick={(e) => e.preventDefault()}>{provider.providerName} <QesIcon iconName={QesIcons.ShareV4} description="open link in new tab" /></a>
                    </TooltipWrapper>
                : <span>{provider.providerName}</span>;

    const showTermedBadge = showTermedStatus && (isTermAdmin || isTermViewer) && !!provider.isTermed && !isHistoricalRunView;
    const providerNameLink = (
      <>
        {baseProviderNameContent}
        {showTermedBadge && (
          <span className="ni-providers-terminated-badge">
            {" ( "}
            <BadgedContent
              badgeStyle={ProviderBadgeStyle.Dot}
              color={QesColors.QesPrimaryRed}
              text="Terminated"
            />
            {" )"}
          </span>
        )}
      </>
    );

    const stripUnknownPrefix = (value: string | null | undefined): string => {
      // State and Specialty fields may have "unknown|" prefix that we want to remove and display the actual data
      if (!value) return '';
      return value.replace(/^unknown\|/i, '');
    }

    return {
      [wafflesKeyColumnName]: `${provider.providerId}|${provider.providerSetVersionId}`,
      npi: provider.npi,
      npiEntityType: toNpiTypeLabel(provider.npiEntityType),
      dateDeactivated: formatDate(provider.dateDeactivated),
      providerName: providerNameLink,
      isServicing: provider.isServicing
        ? "Yes"
        : provider.isExcludedByProjectFilter
          ? "Excluded"
          : "No",
      address1: provider.address1,
      address2: provider.address2,
      city: provider.city,
      state: stripUnknownPrefix(provider.state),
      zip: provider.zip,
      specialty: stripUnknownPrefix(provider.specialty),
      specialtyName: provider.specialtyName || '',
      locationConfidence: showConfidenceLevel ? provider.practitionerAtLocationConfidence : null
    };
  };

  const mergeSearchToMainSelection = useCallback(async (searchState: NetworkIntegrityProviderSelectionState): Promise<NetworkIntegrityProviderSelectionState> => {
    // if search and main selections are the same, no need to merge
    if (searchState.selectionMode === mainSelection.selectionMode &&
        searchState.providerAndProviderSetVersionIds.size === mainSelection.providerAndProviderSetVersionIds.size &&
        [...searchState.providerAndProviderSetVersionIds].every(id => mainSelection.providerAndProviderSetVersionIds.has(id))) {
      return mainSelection;
    }

    if (searchState.providerAndProviderSetVersionIds.size > 0) {
      // Search contains provider ids so user made selections/deselections

      if (searchState.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
        // Search is in Include mode with selections

        if (mainSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
          /*
           * Search is in Include mode with selections, Main is also in Include mode
           *
           * Example:
           * Main Page has some selections (Include Mode, Some Selected)
           * User searches and inherits Include mode with some providers selected
           * User selects more providers in search (Include Mode, Some Selected)
           * The search selections need to be merged back to main selection
           *
           */

          return ( {
            selectionMode: NetworkIntegrityProviderSelectionMode.Include,
            providerAndProviderSetVersionIds:
              new Set([ ...searchState.providerAndProviderSetVersionIds])
          });
        } else {
          /*
           * Search is in Include mode with selections, Main is in Exclude mode
           *
           * Example:
           * Main Page is Select All but has some providers deselected (Exclude Mode, Some Deselected)
           * User searches, inherits Exclude mode with some providers deselected
           * User selects providers that were originally excluded (Include Mode, Some Selected)
           * The Main Selection state needs updated to remove the newly selected from the exclusions and include the newly deselected to the exclusions
           *
           */

          // get provider ids for exclude case because it will be easier to merge
          const selectedIds = await networkIntegrityService.getProviderIds(
            Number(projectId),
            networkIntegrityContext.projectRunConfigurationId,
            networkIntegrityContext.providerType,
            aggregateGroup,
            aggregateType,
            toSelectionDto({
              selectionMode: NetworkIntegrityProviderSelectionMode.Exclude,
              providerAndProviderSetVersionIds: searchState.providerAndProviderSetVersionIds
            }, searchTerm, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, /*actionStatus*/ undefined, npiEntityTypeFilter)
          );

          const filteredSelectedIds = selectedIds.filter(id => !searchState.providerAndProviderSetVersionIds.has(id.toString()));

          const tmpMainSelection: NetworkIntegrityProviderSelectionState = ({
            selectionMode: NetworkIntegrityProviderSelectionMode.Exclude,
            providerAndProviderSetVersionIds: new Set([...filteredSelectedIds])
          });
          return tmpMainSelection;
        }
      } else {
        // Search was in Exclude mode with selections

        if (mainSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
          /*
           * Search is in Exclude mode with selections, Main is in Include mode
           *
           * Example:
           * Main Page has some selections (Include Mode, Some Selected)
           * User searches, inherits Include mode with some providers selected
           * User switches to Select All in search (Exclude Mode, Some Deselected)
           * User deselects some providers that were originally selected (Exclude Mode, Some Deselected)
           * The Main Selection state needs updated to remove the new deselected and include the new selected
           *
           */

          const selectedIds = await networkIntegrityService.getProviderIds(
            Number(projectId),
            networkIntegrityContext.projectRunConfigurationId,
            networkIntegrityContext.providerType,
            aggregateGroup,
            aggregateType,
            toSelectionDto(searchState, searchTerm, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, /*actionStatus*/ undefined, npiEntityTypeFilter)
          );

          return {
            selectionMode: NetworkIntegrityProviderSelectionMode.Include,
            providerAndProviderSetVersionIds: new Set([...mainSelection.providerAndProviderSetVersionIds, ...selectedIds].filter(id => !searchState.providerAndProviderSetVersionIds.has(id.toString())))
          };
        } else {
          /*
           * Search is in Exclude mode with selections, Main is also in Exclude
           *
           * Example:
           * Main Page is Select All but has providers deselected (Exclude Mode, Some Deselected)
           * User searches, inherits Exclude mode with some providers deselected
           * User selects providers that were originally excluded (Exclude Mode, Some Deselected)
           * User deselects providers that were originally selected (Exclude Mode, Some Deselected)
           * The Main Selection state needs updated to include the new selections and remove the new deselected
           *
           */

          // delete newly selected search ids from main exclusions
          mainSelection.providerAndProviderSetVersionIds.forEach(searchId => {
            if (!searchState.providerAndProviderSetVersionIds.has(searchId)) {
              mainSelection.providerAndProviderSetVersionIds.delete(searchId);
            }
          });

          // add newly deselected search ids to main exclusions
          searchState.providerAndProviderSetVersionIds.forEach(searchId => {
            if (!mainSelection.providerAndProviderSetVersionIds.has(searchId)) {
              mainSelection.providerAndProviderSetVersionIds.add(searchId);
            }
          });

          return {
            selectionMode: NetworkIntegrityProviderSelectionMode.Exclude,
            providerAndProviderSetVersionIds: new Set([...mainSelection.providerAndProviderSetVersionIds])
          };
        }
      }
    } else if (searchState.providerAndProviderSetVersionIds.size === 0) {
      // If search has no provider ids its either in a Select All or None Selected state

      // since there are no specific ids in the search selection state, we need to resolve those ids
      const selectedIds = await networkIntegrityService.getProviderIds(
        Number(projectId),
        networkIntegrityContext.projectRunConfigurationId,
        networkIntegrityContext.providerType,
        aggregateGroup,
        aggregateType,
        toSelectionDto(searchState, searchTerm, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, /*actionStatus*/ undefined, npiEntityTypeFilter)
      );

      if (searchState.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
        // Search is in Include mode with no selections so this is a None Selected state

        if (mainSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Exclude) {
          /*
           * Search is in Include mode with no selections, Main was in Exclude mode
           *
           * Example:
           * Main Page is Select All (Exclude Mode, None Deselected)
           * User searches, inherits Exclude Mode and all providers are selected
           * User deselects all in search (Include Mode, None Selected)
           * The providers that were deselected in search need to be added to the Main Selection exclusions
           *
           */

          return {
            selectionMode: NetworkIntegrityProviderSelectionMode.Exclude,
            providerAndProviderSetVersionIds:
              new Set([...mainSelection.providerAndProviderSetVersionIds, ...selectedIds].filter(id => !searchState.providerAndProviderSetVersionIds.has(id.toString())))
          };
        } else {
          /*
           * Search is in Include mode with no selections, Main was in Include mode
           *
           * Example:
           * Main Page has some selections (Include Mode, Some Selected)
           * User searches, inherits Include Mode and contains checked providers based on Main Selection state
           * User deselects all in search (Include Mode, None Selected)
           * The providers that were deselected in search need to be removed from the Main Selection inclusions
           *
           */

          selectedIds.forEach(id => mainSelection.providerAndProviderSetVersionIds.delete(id));
          return {
            selectionMode: NetworkIntegrityProviderSelectionMode.Include,
            providerAndProviderSetVersionIds: mainSelection.providerAndProviderSetVersionIds
          };
        }
      } else {
        // Search is in Exclude mode with no deselections so this is a Select All state

        if (mainSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
          /*
           * Search is in Exclude mode with no deselections, Main was in Include mode
           *
           * Example:
           * Main Page has some selections (Include Mode, Some Selected)
           * User searches, inherits Include mode and contains checked providers based on Main Selection state
           * User selects all in search (Exclude Mode, None Deselected)
           * The providers that are now selected in search need to be added to the Main Selection inclusions
           *
           */

          return {
            selectionMode: NetworkIntegrityProviderSelectionMode.Include,
            providerAndProviderSetVersionIds:
              new Set([...mainSelection.providerAndProviderSetVersionIds, ...selectedIds].filter(id => !searchState.providerAndProviderSetVersionIds.has(id.toString())))
          };
        } else {
          /*
           * Search is in Exclude mode with no deselections, main was in Exclude mode
           *
           * Example:
           * Main Page is Select All but has some providers deselected (Exclude Mode, Some Deselected)
           * User searches, inherits Exclude mode and contains checked providers based on Main Selection state
           * User selects all in search (Exclude Mode, None Deselected)
           * The providers that are newly selected need to be removed from the Main Selection exclusions
           *
           */

          selectedIds.forEach(id => mainSelection.providerAndProviderSetVersionIds.delete(id.toString()));
          return {
            selectionMode: NetworkIntegrityProviderSelectionMode.Exclude,
            providerAndProviderSetVersionIds:
              new Set([...mainSelection.providerAndProviderSetVersionIds])
          };
        }
      }
    }
  }, [mainSelection, projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, aggregateGroup, aggregateType, searchTerm, npiEntityTypeFilter]);

  const handleSearchClear = useCallback(async () => {
    setSearchTerm("");
    setSearchSelection(null);
    updateBulkCheckboxState(mainSelection);
  }, [mainSelection]);

  const handleSearchSubmit = useCallback(async (term: string) => {
    const trimmedTerm = term.trim();

    if (!trimmedTerm) {
      await handleSearchClear();
      return;
    }

    if (trimmedTerm.toLowerCase() === searchTerm.toLowerCase()) {
      return;
    }

    // User is entering/changing search

    setSearchTerm(trimmedTerm);
    setCurrentPage(0);

    // Search selection state inherits main selection state on search change
    const newSearchSelection: NetworkIntegrityProviderSelectionState = {
      providerAndProviderSetVersionIds: mainSelection.providerAndProviderSetVersionIds,
      selectionMode: mainSelection.selectionMode
    };
    setSearchSelection(newSearchSelection);
    updateBulkCheckboxState(mainSelection);
  }, [searchTerm, handleSearchClear, mainSelection]);

  const updateBulkCheckboxState = (selection: NetworkIntegrityProviderSelectionState) => {
    if (selection.selectionMode === NetworkIntegrityProviderSelectionMode.Exclude &&
        selection.providerAndProviderSetVersionIds.size === 0) {
      // All selected
      setBulkCheckboxCheckedOverride(true);
      setBulkCheckboxIndeterminateOverride(false);
    } else if (selection.selectionMode === NetworkIntegrityProviderSelectionMode.Include &&
               selection.providerAndProviderSetVersionIds.size === 0) {
      // None selected
      setBulkCheckboxCheckedOverride(false);
      setBulkCheckboxIndeterminateOverride(false);
    } else {
      // Some selected (indeterminate)
      setBulkCheckboxCheckedOverride(false);
      setBulkCheckboxIndeterminateOverride(true);
    }
  };

  const shouldLocationConfidenceScoreColumnBeShown = (): boolean => {
    return aggregateType == NetworkIntegrityAggregateKey.LowLocationConfidence
      || aggregateType == NetworkIntegrityAggregateKey.VeryLowLocationConfidence
      || aggregateGroup == NetworkIntegrityAggregateGroup.ExcessAddresses;
  }

  const getTotalSelectedCount = (): number => {
    let totalSelected = 0;

    if (activeSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
      totalSelected = activeSelection.providerAndProviderSetVersionIds.size;
    } else {
      totalSelected = totalCount - activeSelection.providerAndProviderSetVersionIds.size;
    }

    return totalSelected;
  };

  const handleDownloadReport = useCallback(async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const selectedIds = await networkIntegrityService.getProviderIds(
      Number(projectId),
      networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId,
      networkIntegrityContext.providerType,
      aggregateGroup,
      aggregateType,
      toSelectionDto(mainSelection, /*searchTerm*/ undefined, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, networkIntegrityContext.actionStatus, npiEntityTypeFilter)
    );

    history.push({
      pathname: `/projects/${projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/reports/${networkIntegrityContext.providerType}/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(aggregateType)}`,
      state: {
        reportRowCount: selectedIds.length,
        runConfiguration: networkIntegrityContext.projectRunConfiguration,
        selectedProviderAndProviderSetVersionIds: selectedIds,
        showConfidenceLevel: showConfidenceLevel
      }
    });
  }, [mainSelection, projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, aggregateGroup, aggregateType, networkIntegrityContext.projectRunConfiguration, networkIntegrityContext.actionStatus, npiEntityTypeFilter, showConfidenceLevel, history]);

  const renderTable = () => {
    const tableDataProvider = providers.map((provider) => getTableDataItem(provider));

    const columnConfig: ColumnConfig = {
        npi: {
            displayText: "NPI",
            hidden: aggregateType == NetworkIntegrityAggregateKey.MissingNpi,
            maxWidth: StandardColumnWidths.npi,
            minWidth: StandardColumnWidths.npi,
            testId: "provider-npi",
            sizeContentToFit: false
        },
        npiEntityType: {
            displayText: "NPI Type",
            hidden: !showNpiType,
            maxWidth: StandardColumnWidths.npiType,
            minWidth: StandardColumnWidths.npiType,
            testId: "provider-npi-type",
            sizeContentToFit: false
        },
        dateDeactivated: {
            displayText: "Date Deactivated",
            hidden: aggregateType != NetworkIntegrityAggregateKey.DeactivatedNpi,
            maxWidth: StandardColumnWidths.dateDeactivated,
            minWidth: StandardColumnWidths.dateDeactivated,
            testId: "provider-date-deactivated",
        },
        providerName: {
            displayText: "Name",
            maxWidth: StandardColumnWidths.providerName,
            minWidth: StandardColumnWidths.providerName,
            testId: "provider-name",
        },
        isServicing: {
          displayText: "Servicing Provider",
          testId: "is-servicing",
          sizeContentToFit: false
        },
        address1: {
            displayText: "Address 1",
            maxWidth: StandardColumnWidths.address1,
            minWidth: StandardColumnWidths.address1,
            testId: "provider-address",
        },
        address2: {
            displayText: "Address 2",
            maxWidth: StandardColumnWidths.address2,
            minWidth: StandardColumnWidths.address2,
            testId: "provider-address2",
        },
        city: {
            displayText: "City",
            maxWidth: StandardColumnWidths.city,
            minWidth: StandardColumnWidths.city,
            testId: "provider-city",
        },
        state: {
            displayText: "State",
            testId: "provider-address",
            sizeContentToFit: false
        },
        zip: {
            displayText: "Zip",
            maxWidth: StandardColumnWidths.zip,
            minWidth: StandardColumnWidths.zip,
            testId: "provider-address",
            sizeContentToFit: false
        },
        specialty: {
            displayText: "Specialty Code",
            maxWidth: StandardColumnWidths.specialtyCode,
            minWidth: StandardColumnWidths.specialtyCode,
            testId: "provider-specialty",
        },
        specialtyName: {
            displayText: "Specialty Name",
            maxWidth: StandardColumnWidths.specialtyName,
            minWidth: StandardColumnWidths.specialtyName,
            testId: "provider-specialty-name",
        },
        locationConfidence: {
            displayText: "Practitioner at Location Confidence Level",
            hidden: !shouldLocationConfidenceScoreColumnBeShown(),
            testId: "location-confidence"
        }
    };
    /* oxlint-enable sort-keys */

    const isSelected = (id: string): boolean => {
      return activeSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Exclude
        ? !activeSelection.providerAndProviderSetVersionIds.has(id)
        : activeSelection.providerAndProviderSetVersionIds.has(id);
    };

    const getSelectedKeysForCurrentPage = (): string[] => {
      return providers
        .map(p => `${p.providerId}|${p.providerSetVersionId}`)
        .filter(id => isSelected(id));
    };

    const onCheckboxChange = async (newSelectedKeys: string[], isBulk: boolean) => {
      if (isBulk) {
        // Toggle between Select All and Deselect All
        const isAllSelected = activeSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Exclude &&
                              activeSelection.providerAndProviderSetVersionIds.size === 0;
        const isNoneSelected = activeSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include &&
                               activeSelection.providerAndProviderSetVersionIds.size === 0;

        let newState: NetworkIntegrityProviderSelectionState;

        if (isAllSelected) {
          // Currently all selected, switch to none selected
          newState = {
            providerAndProviderSetVersionIds: new Set(),
            selectionMode: NetworkIntegrityProviderSelectionMode.Include
          };
        } else if (isNoneSelected) {
          // Currently none selected, switch to all selected
          newState = {
            providerAndProviderSetVersionIds: new Set(),
            selectionMode: NetworkIntegrityProviderSelectionMode.Exclude
          };
        } else {
          // Indeterminate state - select all
          newState = {
            providerAndProviderSetVersionIds: new Set(),
            selectionMode: NetworkIntegrityProviderSelectionMode.Exclude
          };
        }

        if (isSearchActive) {
          setSearchSelection(newState);
          updateBulkCheckboxState(newState);

          const mergedMainSelection = await mergeSearchToMainSelection(newState);
          setMainSelection(mergedMainSelection);
        } else {
          setMainSelection(newState);
          updateBulkCheckboxState(newState);
        }
        return;
      }

      // Individual checkbox change - determine which ID toggled

      const previouslySelected = getSelectedKeysForCurrentPage();

      const updateSelectionStateIdsWithSelectedId = (currentSelection: NetworkIntegrityProviderSelectionState): NetworkIntegrityProviderSelectionState => {
        if (!currentSelection) return currentSelection;

        // get the single id that was toggled
        const symmetricDifference = previouslySelected
          .filter(x => !newSelectedKeys.includes(x))
          .concat(newSelectedKeys.filter(x => !previouslySelected.includes(x)));

        if (previouslySelected.length < newSelectedKeys.length) {  // id added
          if (currentSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
            // Include mode - add to set
            return { ...currentSelection, providerAndProviderSetVersionIds: currentSelection.providerAndProviderSetVersionIds.add(symmetricDifference[0]) };
          } else {
            // Exclude mode - remove from set
            const newIds = new Set(currentSelection.providerAndProviderSetVersionIds);
            symmetricDifference.forEach(id => newIds.delete(id));
            return { ...currentSelection, providerAndProviderSetVersionIds: newIds };
          }
        } else if (previouslySelected.length > newSelectedKeys.length) { // id removed
          if (currentSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
            // Include mode - remove from set
            const newIds = new Set(currentSelection.providerAndProviderSetVersionIds);
            symmetricDifference.forEach(id => newIds.delete(id));
            return { ...currentSelection, providerAndProviderSetVersionIds: newIds };
          } else {
            // Exclude mode - add to set
            return { ...currentSelection, providerAndProviderSetVersionIds: currentSelection.providerAndProviderSetVersionIds.add(symmetricDifference[0]) };
          }
        } else {
          return currentSelection;
        }
      };

      if (isSearchActive) {
        const updatedSearchSelection = updateSelectionStateIdsWithSelectedId(searchSelection);
        setSearchSelection(updatedSearchSelection);

        const mergedMainSelection = await mergeSearchToMainSelection(updatedSearchSelection);
        setMainSelection(mergedMainSelection);

        updateBulkCheckboxState(updatedSearchSelection);
      } else {
        const updatedMainSelection = updateSelectionStateIdsWithSelectedId(mainSelection);
        setMainSelection(updatedMainSelection);
        updateBulkCheckboxState(updatedMainSelection);
      }
    };

    const showTerminateProvider = () => {
      if (projectIsRunning)
        return false;

      if (!networkIntegrityContext.projectRunConfiguration?.datePublished)
        return false;

      if (isHistoricalRunView)
        return false;

      if (!isTermAdmin)
        return false;

      if (networkIntegrityContext.providerType == NetworkIntegrityProviderTypes.Prospect
        || networkIntegrityContext.providerType == NetworkIntegrityProviderTypes.ProspectServicing)
        return false;

      switch (aggregateGroup) {
        case NetworkIntegrityAggregateGroup.ProvidersByCounty:
        case NetworkIntegrityAggregateGroup.ProvidersByZipCode:
        case NetworkIntegrityAggregateGroup.ProvidersBySpecialty:
        case NetworkIntegrityAggregateGroup.ProvidersByState:
          return false;
        case NetworkIntegrityAggregateGroup.ProvidersByGeocode:
          return aggregateType == NetworkIntegrityAggregateKey.GeoCodeZipDistributive;
      }

      switch (aggregateType) {
        case NetworkIntegrityAggregateKey.DeactivatedNpi:
          return !(networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.DeactivatedProvider) &&
            networkIntegrityContext.projectRunConfiguration.networkExclusions?.includes(ProjectExclusionType.DeactivatedFacility));
        case NetworkIntegrityAggregateKey.InvalidNpi:
          return !(networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.InvalidNppesNpiProvider) &&
            networkIntegrityContext.projectRunConfiguration.networkExclusions?.includes(ProjectExclusionType.InvalidNppesNpiFacility));
        case NetworkIntegrityAggregateKey.MedicareOptOut:
          return !networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.MedicareOptOut);
        case NetworkIntegrityAggregateKey.MedicarePrecluded:
          return !networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.Precluded);
        case NetworkIntegrityAggregateKey.MissingNpi:
          return !(networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.MissingNpiProvider) &&
            networkIntegrityContext.projectRunConfiguration.networkExclusions?.includes(ProjectExclusionType.MissingNpiFacility));
        case NetworkIntegrityAggregateKey.OigExclusion:
          return !networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.OIGExclusion);
        default:
          return true;
      }
    }

    const getMainSelectionCount = (): number => {
      if (mainSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include) {
        return mainSelection.providerAndProviderSetVersionIds.size;
      } else {
        return totalCount - mainSelection.providerAndProviderSetVersionIds.size;
      }
    };

    const getProvidersTerminationFlyout = (): JSX.Element => {
      return (
        <NetworkIntegrityTerminationFlyout
          aggregateGroup={aggregateGroup}
          aggregateType={aggregateType}
          projectId={Number(projectId)}
          fetchSelectedProviders={fetchSelectedProviders}
          selectionCount={getMainSelectionCount()}
          projectRunConfigurationDualType={networkIntegrityContext.projectRunConfigurationDualType }
        />
      );
    };

    const wafflesCustomSort = (segment: NetworkIntegrityProviderTableData[], column: keyof NetworkIntegrityProviderTableData, direction: SortDirections): NetworkIntegrityProviderTableData[] => {
      if (direction === SortDirections.None) {
        setSortedColumn("npi");
        setSortDirection("asc");
      } else if (direction === SortDirections.Asc) {
        setSortedColumn(column.toString());
        setSortDirection("asc");
      } else if (direction === SortDirections.Desc) {
        setSortedColumn(column.toString());
        setSortDirection("desc");
      }
      return segment; // segment is already sorted on the server
    };

    const selectionCount = getTotalSelectedCount();

    const selectedProviderKeys = [...mainSelection.providerAndProviderSetVersionIds];
    // Exclude mode means "everything but these", which cannot be enumerated from the loaded page.
    const onlyTermedSelected = mainSelection.selectionMode === NetworkIntegrityProviderSelectionMode.Include
      && selectedProviderKeys.length > 0
      && selectedProviderKeys.every(key => termedProviderKeys.has(key));
    const hasSelection = getMainSelectionCount() > 0;
    const blockedByTermed = hasSelection
      && (networkIntegrityContext.actionStatus === NetworkIntegrityActionStatus.Terminated || onlyTermedSelected);
    const isTerminateDisabled = blockedByTermed || !hasSelection;

    const bulkActionBar = (
      <>
        {showTerminateProvider() && (
          <TooltipWrapper text={blockedByTermed ? "All selected access points have already been terminated." : ""}>
            <FlyoutLink
              classNames="qes-button qes-button-secondary"
              content={{
                body: getProvidersTerminationFlyout(),
                title: "Terminate Providers",
              }}
              disabled={isTerminateDisabled}
              panelId={`ni-terminate-provider__flyout-panel`}
              size="60"
              testId={`ni-terminate-provider__flyout-panel-test`}
            >
              Terminate Providers
            </FlyoutLink>
          </TooltipWrapper>
        )}
        <Link
          className="qes-button qes-button-secondary"
          id="network-integrity-providers__download-report-link"
          to={{
            pathname: `/projects/${projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/reports/${networkIntegrityContext.providerType}/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(aggregateType)}`,
            state: {
              reportRowCount: selectionCount > 0 ? selectionCount : totalCount,
              runConfiguration: networkIntegrityContext.projectRunConfiguration,
              showConfidenceLevel: showConfidenceLevel
            }
          }}
          type="button"
          onClick={handleDownloadReport}
        >
          Download Report
        </Link>
      </>
    );

    const getSortDirectionOverride = () => {
      if (sortDirection === "asc" && sortedColumn === "npi") {
        return SortDirections.None;
      } else if (sortDirection === "none") {
        return SortDirections.None;
      } else if (sortDirection === "asc") {
        return SortDirections.Asc;
      } else {
        return SortDirections.Desc;
      }
    }

    const uniqueAccessPointDisplay = searchTerm ? '' : `(${uniqueAccessPointCount.toLocaleString()} unique access points)`;

    return ( !networkIntegrityContext.isLoading && isInitialized &&
      <div className="row">
        <div className="col-12">
            {!loading && <p className="mb5">Displaying {(currentPage * pageSize + 1).toLocaleString()} - {Math.min((currentPage + 1) * pageSize, totalCount).toLocaleString()} of {totalCount.toLocaleString()} total rows of data {uniqueAccessPointDisplay}</p>}
          <Waffles<NetworkIntegrityProviderTableData>
            columns={columnConfig}
            passThroughSorting={true}
            customSort={wafflesCustomSort}
            sortDirectionOverride={getSortDirectionOverride()}
            sortedColumnOverride={sortedColumn as keyof NetworkIntegrityProviderTableData}
            bulkActionBar={tableDataProvider.length > 0 && bulkActionBar}
            data={tableDataProvider}
            testId={`${aggregateType}-provider-table`}
            showCheckboxes={true}
            handleCheckboxChange={onCheckboxChange}
            bulkCheckboxCheckedOverride={bulkCheckboxCheckedOverride}
            bulkCheckboxIndeterminateOverride={bulkCheckboxIndeterminateOverride}
            selectedKeys={getSelectedKeysForCurrentPage()}
            loading={loading}
            totalNumberOfRecords={totalCount}
            enablePagination={true}
            paginationProps={{
              changePage: (pageNumber: number) => { setCurrentPage(pageNumber-1) },
              recordsPerPage: pageSize,
              skip: (currentPage) * pageSize,
              totalRecords: totalCount
            }}
          />
        </div>
      </div>
    );
  };

  const parseAggregateKey = (aggregateKey: string) => {
    const keyParts = aggregateKey.split('|');
    const isUnknown = keyParts[0]?.toLowerCase() === 'unknown';

    return { isUnknown, keyParts };
  }

  const generateDefaultTitle = (aggregateKey: string) => {
    const { isUnknown, keyParts } = parseAggregateKey(aggregateKey);

    switch (aggregateGroup) {
      case NetworkIntegrityAggregateGroup.ProvidersByCounty:
        return isUnknown ? `Unknown - ${keyParts[1] || 'No Value'}` : keyParts[1];
      case NetworkIntegrityAggregateGroup.ProvidersByGeocode:
        return aggregateKey;
      case NetworkIntegrityAggregateGroup.ProvidersBySpecialty:
        return isUnknown ? `Unknown - ${keyParts[1] || 'No Value'}` : `${keyParts[0]} - ${keyParts[1]}`;
      case NetworkIntegrityAggregateGroup.ProvidersByState:
        return isUnknown ? `Unknown - ${keyParts[1] || 'No Value'}` : keyParts[1];
      case NetworkIntegrityAggregateGroup.ExcessAddresses:
        return `Providers With ${aggregateKey} Addresses`;
      case NetworkIntegrityAggregateGroup.ExcessSpecialties:
        return `Providers With ${aggregateKey} Specialties`;
      default:
        return aggregateKey;
    }
  }

  const getTitle = () => {
    let title = ""
    switch (aggregateGroup) {
      case NetworkIntegrityAggregateGroup.OptionalExclusions:
        title += "Project Option Exclusions: ";
        break;
      case NetworkIntegrityAggregateGroup.CriticalAlerts:
        title += "Critical Integrity Alerts: ";
        break;
      case NetworkIntegrityAggregateGroup.AttestationBasedAlerts:
        title += "Attestation Based Alerts: ";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByCounty:
        title += "Providers By County: ";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByZipCode:
        title += "Providers By Zip Code: ";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByGeocode:
        title += "Providers By Geocode: ";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersBySpecialty:
        title += "Providers By Specialty: ";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByState:
        title += "Providers By State: ";
        break;
      case NetworkIntegrityAggregateGroup.ExcessAddresses:
      case NetworkIntegrityAggregateGroup.ExcessSpecialties:
        title += "Provider Detail: ";
        break;
      case NetworkIntegrityAggregateGroup.ProvidersByZipClass:
        title += "Providers By Zip Code Classification: ";
        break;
    };

    switch (aggregateType) {
      case NetworkIntegrityAggregateKey.DeactivatedNpi:
        title += "Deactived NPI";
        break;
      case NetworkIntegrityAggregateKey.ExpiredLicensePractitioners:
        title += "Expired License Practitioners";
        break;
      case NetworkIntegrityAggregateKey.GhostProviders:
        title += "Ghost Providers (Total Claims)";
        break;
      case NetworkIntegrityAggregateKey.InvalidNpi:
        title += "Invalid NPI";
        break;
      case NetworkIntegrityAggregateKey.LeftOffice:
        title += "Left Office";
        break;
      case NetworkIntegrityAggregateKey.MedicareOptOut:
        title += "Medicare Opt Out";
        break;
      case NetworkIntegrityAggregateKey.MedicarePrecluded:
        title += "Medicare Preclusion";
        break;
      case NetworkIntegrityAggregateKey.MissingNpi:
        title += "Missing NPI";
        break;
      case NetworkIntegrityAggregateKey.OigExclusion:
        title += "OIG Exclusion";
        break;
      case NetworkIntegrityAggregateKey.RetiredPractitioner:
        title += "Retired Practitioner";
        break;
      case NetworkIntegrityAggregateKey.LowLocationConfidence:
        title += "Low Location Confidence";
        break;
      case NetworkIntegrityAggregateKey.VeryLowLocationConfidence:
        title += "Very Low Location Confidence";
        break;
      case NetworkIntegrityAggregateKey.NoLongerOnRoster:
        title += "No Longer on Roster";
        break;
      case NetworkIntegrityAggregateKey.ClosedLocation:
        title += "Closed Location";
        break;
      case NetworkIntegrityAggregateKey.NotRecognized:
        title += "Not Recognized";
        break;
      case NetworkIntegrityAggregateKey.TermedOnRoster:
        title += "Termed On Roster";
        break;
      default:
        title += generateDefaultTitle(aggregateType);
        break;
    };

    return title
  }

  if (showForbiddenAlert) {
    return (
      Forbidden()
    );
  }

  if (!validAggregateKey) {
    return (
      NotFound()
    );
  }

  return (
    <Page documentTitle="Network Integrity Providers">
      <PageInner>
        <PageHeader title={`${getTitle()}`}>
          <PageHeaderRight>
            <Link
              className="qes-button qes-button-secondary"
              type="button"
              id="network-integrity-providers__back"
              to={{ pathname: `/projects/${projectId}/network-integrity`, state: { initialProviderType: networkIntegrityContext.providerType, initialRegionSetIds: networkIntegrityContext.regionSetIds, initialSpecialtyCodes: networkIntegrityContext.specialtyCodes, runConfiguration: networkIntegrityContext.projectRunConfiguration } }}
            >
              Return to Project
            </Link>
          </PageHeaderRight>
        </PageHeader>

        <PageSection>
          <div className="col-12 col-lg-8 ps-0">
            <SearchBox
              key={`${networkIntegrityContext.providerType}-${networkIntegrityContext.projectRunConfigurationId}`}
              descriptiveText="Search provider's NPI, name, address, or specialty"
              fieldsetClass="d-flex"
              inputLabel="Provider Search"
              onSubmit={handleSearchSubmit}
              testId="network-integrity-provider__search"
            />
          </div>
        </PageSection>

        <PageSection>
          <NetworkIntegrityFilterContainer isProviderDetailPage />
        </PageSection>

        {loading &&
          <PageSection>
            <LoadingSpinner />
          </PageSection>
        }
        {!loading &&
          <PageSection>
            {renderTable()}
          </PageSection>
        }

      </PageInner>
    </Page>
  );
}

export { NetworkIntegrityProvidersPage };